import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { api } from './api';

// The Autoexecutor's turn as it happens, at the bottom of a chat
// (tasks/live.js on the server): queued, then every step — its thinking
// (folded), each tool call in plain words with how it ended, the reply as it
// is written — with a clock. When the turn ends, the reply arrives as a chat
// message and the steps fold into one line above it (StepsFold).
//
// It also says what the silence means: the agent thinking (the clock runs,
// signs of life arrive), the turn gone quiet on the server (no sign for
// QUIET_MS — Stop is offered), or this browser off the server (the stream is
// down — App.js rebuilds it; what was missed is read again on reconnect).

const QUIET_MS = 70000;               // the server sends a sign of life every 30 s
const END_LINGER_MS = 5000;           // an ended turn waits this long for its message

const TOOL_LABELS = {
  get_task: 'Reading the card',
  list_points: 'Listing the points',
  read_point: 'Reading a point',
  find_word: 'Finding a word in the quest',
  search_text: 'Searching the quest text',
  list_takes: 'Listing the takes',
  get_glossary: 'Looking up the glossary',
  set_glossary_entry: 'Writing to the glossary',
  set_respell: 'Writing a replacement rule for TTS',
  set_stress_here: 'Setting the stress in this place only',
  list_recipes: 'Listing the recipes',
  run_recipe: 'Starting a recipe',
  get_run: 'Checking an attempt',
  stop_run: 'Stopping an attempt',
  propose_text: 'Proposing a text edit',
  add_target: 'Adding a place to the card',
  measure_loudness: 'Measuring loudness',
  find_pauses: 'Looking for long pauses',
  read_card: 'Reading a card',
  note_card: 'Writing a line into a card',
  start_revoice: 'Starting the re-voice',
  propose_card: 'Proposing a new card',
  hand_to_card: 'Handing the remark to a card',
  get_draft: 'Reading the Draft',
  draft_edit: 'Editing the Draft',
  add_turn_note: 'Adding a remark to the turn',
  update_card: 'Updating a card',
  ToolSearch: 'Loading its tools',
  WebSearch: 'Searching the web',
  web_search: 'Searching the web',
  WebFetch: 'Opening a web page',
  web_fetch: 'Opening a web page',
};

// What the call was asked, short: the values of its input (the glossary
// entry as «form → stressed»), or the raw detail when it is not JSON.
function toolArgs(seg) {
  const raw = seg.detail || '';
  if (!raw) return '';
  let a = null;
  try { a = JSON.parse(raw); } catch (e) { return raw; }
  if (!a || typeof a !== 'object') return String(a);
  if (seg.name === 'set_glossary_entry' && a.form) return `${a.form} → ${a.stressed || '?'}`;
  if (seg.name === 'ToolSearch') return '';
  return Object.values(a).filter((v) => v != null && typeof v !== 'object').map(String).join(' · ');
}

const fmtClock = (ms) => { const s = Math.max(0, Math.round(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };
const fmtSecs = (ms) => (ms < 10000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms / 1000)} s`);

function Markdown({ text }) {
  return <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml>{text}</ReactMarkdown>;
}

// One step. `current` — the step in progress of a live turn.
function Segment({ seg, current }) {
  if (seg.t === 'thinking') {
    return (
      <details className="tk-live-think">
        <summary>{current ? 'Thinking…' : 'Thought'} <span className="tk-muted">· {seg.text.length.toLocaleString('en-US')} chars</span></summary>
        <div className="tk-live-think-text">{seg.text}</div>
      </details>
    );
  }
  if (seg.t === 'tool') {
    const label = TOOL_LABELS[seg.name] || seg.name;
    const args = toolArgs(seg);
    const state = seg.error ? '✗' : seg.end ? '✓' : '⋯';
    return (
      <div className={`tk-live-tool ${seg.error ? 'err' : ''}`}>
        <span className="tk-live-mark" aria-label={seg.error ? 'failed' : seg.end ? 'done' : 'in progress'}>{state}</span>
        <span>{label}</span>
        {args ? <span className="tk-live-args" title={args}>{args}</span> : null}
        <span className="tk-muted tk-live-name">{seg.name}{seg.end && seg.at ? ` · ${fmtSecs(seg.end - seg.at)}` : ''}</span>
        {seg.error && seg.result ? <details className="tk-live-errtext"><summary>error text</summary><pre>{seg.result}</pre></details> : null}
      </div>
    );
  }
  if (seg.t === 'status') {
    const [head, ...rest] = String(seg.text || '').split('\n');
    return (
      <div className="tk-live-status tk-muted">
        {head}
        {rest.length ? <details><summary>details</summary><pre>{rest.join('\n')}</pre></details> : null}
      </div>
    );
  }
  if (seg.t === 'text') return <div className="tk-live-text tk-msg-body"><Markdown text={seg.text} /></div>;
  return null;
}

// The steps of a finished turn, folded above its reply (meta.steps).
export function StepsFold({ steps }) {
  const segs = (steps && steps.segs) || [];
  if (!segs.length) return null;
  const calls = segs.filter((s) => s.t === 'tool').length;
  const failed = segs.filter((s) => s.t === 'tool' && s.error).length;
  const parts = [calls ? `${calls} tool call${calls === 1 ? '' : 's'}` : null, failed ? `${failed} failed` : null, segs.some((s) => s.t === 'thinking') ? 'thinking' : null, steps.ms ? fmtSecs(steps.ms) : null].filter(Boolean);
  return (
    <details className="tk-live-fold">
      <summary>Steps · {parts.join(' · ')}</summary>
      <div className="tk-live-segs">{segs.map((s, i) => <Segment key={i} seg={s} current={false} />)}</div>
    </details>
  );
}

export default function AgentLive({ taskId, messages, onGrow }) {
  const [run, setRun] = useState(null);         // the queued / running agent turn (brief)
  const [live, setLive] = useState(null);       // {runId, startedAt, seq, segs, lastAt, endedAt?, endStatus?}
  const [stream, setStream] = useState(() => window.__qvStream || 'open');
  const [now, setNow] = useState(Date.now());
  const [stopping, setStopping] = useState(false);
  const liveRef = useRef(null);
  liveRef.current = live;
  const seqRef = useRef(0);

  const refetch = useCallback(async () => {
    const mine = ++seqRef.current;
    try {
      const r = await api.live(taskId);
      if (mine !== seqRef.current) return;
      setRun(r.run || null);
      // The server's own «last sign» of the turn, moved onto this clock: a
      // read every 2 s (no stream) must not look like a sign of life itself.
      if (r.live) {
        const ago = r.now && r.live.lastAt ? Math.max(0, new Date(r.now).getTime() - new Date(r.live.lastAt).getTime()) : 0;
        setLive({ ...r.live, lastAt: Date.now() - ago });
      }
      else setLive((cur) => (cur && cur.endedAt ? cur : null));
    } catch (e) { /* the next event or resync reads it again */ }
  }, [taskId]);

  useEffect(() => { setRun(null); setLive(null); refetch(); }, [refetch]);

  useEffect(() => {
    const onTasks = (e) => {
      const d = e.detail || {};
      if (d.type === 'task_run' && d.run && d.run.task_id === taskId && (d.run.recipe_id === 'agent' || !d.run.recipe_id)) {
        if (['queued', 'running'].includes(d.run.status)) setRun(d.run);
        else setRun((cur) => (cur && cur.id === d.run.id ? null : cur));
        return;
      }
      if (d.type !== 'task_live' || d.taskId !== taskId) return;
      const cur = liveRef.current;
      if (d.op === 'begin') { setLive({ runId: d.runId, startedAt: d.startedAt, seq: d.seq, segs: [], lastAt: Date.now() }); return; }
      if (!cur || cur.runId !== d.runId || d.seq !== cur.seq + 1 || d.op === 'reset') { refetch(); return; }
      const segs = cur.segs.slice();
      if (d.op === 'seg') segs[d.i] = d.seg;
      else if (d.op === 'add') { if (!segs[d.i]) { refetch(); return; } segs[d.i] = { ...segs[d.i], text: segs[d.i].text + d.text }; }
      const next = { ...cur, segs, seq: d.seq, lastAt: Date.now() };
      if (d.op === 'end') { next.endedAt = Date.now(); next.endStatus = d.status; setRun((r) => (r && r.id === d.runId ? null : r)); }
      setLive(next);
    };
    const onStream = (e) => setStream((e.detail && e.detail.state) || 'open');
    window.addEventListener('qv:tasks', onTasks);
    window.addEventListener('qv:resync', refetch);
    window.addEventListener('qv:poll', refetch);
    window.addEventListener('qv:stream', onStream);
    return () => { window.removeEventListener('qv:tasks', onTasks); window.removeEventListener('qv:resync', refetch); window.removeEventListener('qv:poll', refetch); window.removeEventListener('qv:stream', onStream); };
  }, [taskId, refetch]);

  const repliedTo = live && (messages || []).some((m) => m.meta && m.meta.run_id === live.runId);
  const ended = live && live.endedAt && (repliedTo || now - live.endedAt > END_LINGER_MS);
  const active = !!(run || (live && !ended));

  // The clock (and the «no sign» check) ticks only while there is something to time.
  useEffect(() => {
    if (!active && stream === 'open') return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active, stream]);
  useEffect(() => { if (live && live.endedAt && repliedTo) setLive(null); }, [live, repliedTo]);
  // Without the stream (App polls), a running turn's steps are read every 2 s.
  useEffect(() => {
    if (stream !== 'poll' || !active) return undefined;
    const t = setInterval(refetch, 2000);
    return () => clearInterval(t);
  }, [stream, active, refetch]);
  const liveSeq = live ? live.seq : 0;
  const runStatus = run ? run.status : null;
  useEffect(() => { if (onGrow) onGrow(); }, [liveSeq, runStatus, stream, onGrow]);

  const stop = async () => {
    setStopping(true);
    try { await api.stop(taskId); } catch (e) { /* the run event tells what happened */ } finally { setStopping(false); }
  };

  const offline = stream === 'lost';
  if (!active && !offline) return null;
  if (!active && offline) {
    return <div className="tk-live tk-live-offline" role="status">This page has lost its connection to the server and is reconnecting. New messages will appear once it is back.</div>;
  }

  const queued = run && run.status === 'queued' && !(live && !live.endedAt);
  const startedAt = live && live.startedAt ? new Date(live.startedAt).getTime() : (run ? new Date(run.created_at || Date.now()).getTime() : now);
  const segs = (live && live.segs) || [];
  const last = segs[segs.length - 1];
  const quietFor = live && !live.endedAt ? now - live.lastAt : 0;
  const quiet = !offline && quietFor > QUIET_MS;
  const doing = live && live.endedAt
    ? (live.endStatus === 'done' ? 'Finished. The reply is on its way to the chat.' : live.endStatus === 'cancelled' ? 'Stopped.' : 'Ended with an error. The reason is in the chat.')
    : !live ? (queued ? 'Queued. The agent starts within a few seconds.' : 'Running. Its steps are not shown for this turn.')
      : last && last.t === 'tool' && !last.end ? `${TOOL_LABELS[last.name] || last.name}…`
        : last && last.t === 'text' ? 'Writing the reply…'
          : 'Thinking…';

  return (
    <div className="tk-live" role="status" aria-live="polite">
      <div className="tk-live-head">
        <span className="tk-tag tk-tag-exec">Autoexecutor</span>
        <span className="tk-live-doing">{doing}</span>
        {!(live && live.endedAt) ? <span className="tk-muted tk-live-clock">{fmtClock(now - startedAt)}</span> : null}
      </div>
      {segs.length ? <div className="tk-live-segs">{segs.map((s, i) => <Segment key={i} seg={s} current={!live.endedAt && i === segs.length - 1} />)}</div> : null}
      {offline ? <div className="tk-live-warn">This page has lost its connection to the server and is reconnecting. The turn continues on the server; its steps will appear once the connection is back.</div> : null}
      {quiet ? (
        <div className="tk-live-warn">
          No sign of this turn from the server for {fmtClock(quietFor)}. It may have stopped there. Wait a little longer, or stop it and ask again.
          {' '}<button type="button" className="tk-btn tk-btn-small" onClick={stop} disabled={stopping}>{stopping ? 'Stopping…' : 'Stop this turn'}</button>
        </div>
      ) : null}
    </div>
  );
}
